import { useEffect, useMemo, useRef } from "react";
import { useTranslation } from "react-i18next";
import { Action } from "app/services/types.generated";
import { buildNavigateToOrdinalMap } from "lib/mission/missionMapWaypoints";
import ActionRow, { type LoadMapFilterSelection } from "./ActionRow";

interface ActionListProps {
  actions: Action[];
  onNoteChange?: (actionId: string, newNote: string) => void;
  onSaveMapAutoLoadToggle?: (actionId: string, enabled: boolean) => void;
  onLoadMapFilterChange?: (actionId: string, next: LoadMapFilterSelection) => void;
  arcHost?: string | null;
  editMode?: boolean;
  readOnly?: boolean;
  insertionIndex?: number | null;
  onInsertionIndexChange?: (index: number) => void;
  onRemoveAction?: (actionId: string) => void;
  scrollToActionId?: string | null;
  /** Where to pin the scrolled row. Use "start" while a mission step is executing. */
  scrollBlock?: ScrollLogicalPosition;
  startHereMode?: boolean;
  onStartHereSelect?: (action: Action, index: number) => void;
}

const ActionList: React.FC<ActionListProps> = ({
  actions,
  onNoteChange,
  onSaveMapAutoLoadToggle,
  onLoadMapFilterChange,
  arcHost = null,
  editMode = false,
  insertionIndex = null,
  onInsertionIndexChange,
  onRemoveAction,
  readOnly = false,
  scrollToActionId = null,
  // "end" keeps a newly recorded step at the bottom so a pinned mission-end dock
  // below it stays out of view; execution passes "start" instead.
  scrollBlock = "end",
  startHereMode = false,
  onStartHereSelect,
}) => {
  const { t } = useTranslation("missions");
  const actionRowRefs = useRef<Map<string, HTMLDivElement>>(new Map());
  const lastScrolledActionIdRef = useRef<string | null>(null);
  const lastScrolledIndexRef = useRef<number | null>(null);
  const lastScrollBlockRef = useRef<ScrollLogicalPosition | null>(null);

  const setActionRowRef = (actionId: string) => (element: HTMLDivElement | null) => {
    if (element) {
      actionRowRefs.current.set(actionId, element);
    } else {
      actionRowRefs.current.delete(actionId);
    }
  };

  useEffect(() => {
    if (!scrollToActionId) return;
    const targetIndex = actions.findIndex((action) => action.id === scrollToActionId);
    if (targetIndex < 0) return;
    // Re-scroll when the same id moves (e.g. inserted before mission-end dock)
    // or when alignment changes (recording "end" → executing "start").
    if (
      lastScrolledActionIdRef.current === scrollToActionId &&
      lastScrolledIndexRef.current === targetIndex &&
      lastScrollBlockRef.current === scrollBlock
    ) {
      return;
    }

    lastScrolledActionIdRef.current = scrollToActionId;
    lastScrolledIndexRef.current = targetIndex;
    lastScrollBlockRef.current = scrollBlock;
    const frame = requestAnimationFrame(() => {
      actionRowRefs.current.get(scrollToActionId)?.scrollIntoView({
        behavior: "smooth",
        block: scrollBlock,
      });
    });

    return () => cancelAnimationFrame(frame);
  }, [scrollToActionId, actions, scrollBlock]);

  useEffect(() => {
    if (scrollToActionId) return;
    lastScrolledActionIdRef.current = null;
    lastScrolledIndexRef.current = null;
    lastScrollBlockRef.current = null;
  }, [scrollToActionId]);

  const navigateToOrdinalById = useMemo(
    () => buildNavigateToOrdinalMap(actions),
    [actions],
  );

  if (actions.length === 0) {
    return (
      <div className="flex h-full items-center justify-center py-8">
        <span className="text-h5 text-black60">
          {readOnly ? "No actions." : "No actions yet. Add actions using buttons below."}
        </span>
      </div>
    );
  }

  const insertButtonClass = (active: boolean) =>
    `h-6 w-6 rounded-full border text-h5 leading-none transition-colors ${
      active
        ? "border-primary2 bg-primary2 text-white"
        : "border-black60 bg-black80 text-black40 hover:border-primary2 hover:text-white"
    }`;

  return (
    <div className="flex flex-col w-full">
      {editMode && !readOnly ? (
        <div className="flex justify-center py-1">
          <button
            type="button"
            className={insertButtonClass(insertionIndex === 0)}
            title={t("createEdit.insertAtStart")}
            aria-label={t("createEdit.insertAtStart")}
            // Re-click exits insert mode (position persists after each insert).
            onClick={() => onInsertionIndexChange?.(insertionIndex === 0 ? null : 0)}
          >
            +
          </button>
        </div>
      ) : null}
      {actions.map((action, index) => (
        <div key={action.id} ref={setActionRowRef(action.id)}>
          <ActionRow
            action={action}
            navigateToOrdinal={navigateToOrdinalById.get(action.id)}
            onNoteChange={onNoteChange}
            onSaveMapAutoLoadToggle={onSaveMapAutoLoadToggle}
            onLoadMapFilterChange={onLoadMapFilterChange}
            arcHost={arcHost}
            showRemoveButton={editMode && !readOnly}
            readOnly={readOnly}
            editing={editMode}
            onRemove={onRemoveAction}
            startHereMode={startHereMode}
            onStartHereSelect={
              startHereMode && onStartHereSelect
                ? () => onStartHereSelect(action, index)
                : undefined
            }
          />
          {editMode && !readOnly && index < actions.length - 1 && (
            <div className="flex justify-center py-1">
            <button
              type="button"
              className={insertButtonClass(insertionIndex === index + 1)}
              title={t("createEdit.insertHere")}
              aria-label={t("createEdit.insertHere")}
              // Re-click exits insert mode (position persists after each insert).
              onClick={() =>
                onInsertionIndexChange?.(insertionIndex === index + 1 ? null : index + 1)
              }
            >
                +
              </button>
            </div>
          )}
        </div>
      ))}
    </div>
  );
};

export default ActionList;
